import { Box, Paper, Stack, Typography } from '@mui/material';
import { useId, type ReactNode } from 'react';

export interface SectionCardProps {
  readonly action?: ReactNode;
  readonly children: ReactNode;
  readonly headingLevel?: 'h2' | 'h3';
  readonly subtitle?: ReactNode;
  readonly title: ReactNode;
}

export function SectionCard({
  action,
  children,
  headingLevel = 'h2',
  subtitle,
  title,
}: SectionCardProps) {
  // A <section> is only exposed as a landmark region when it has an accessible name, so the heading
  // names it. Dashboard tests locate cards by getByRole('region', { name }).
  const headingId = useId();

  return (
    <Paper
      aria-labelledby={headingId}
      component="section"
      variant="outlined"
      sx={{ minWidth: 0, p: { xs: 2, md: 2.5 } }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{
          alignItems: { sm: 'flex-start' },
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component={headingLevel} id={headingId} variant="h5">
            {title}
          </Typography>
          {subtitle === undefined ? null : (
            <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="body2">
              {subtitle}
            </Typography>
          )}
        </Box>
        {action === undefined ? null : <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Stack>
      {children}
    </Paper>
  );
}
